<template>
  <div>
    <empty-state
      v-if="!restaurantIds"
      icon="mdi-currency-usd"
      :title="$t('select_restaurant')"
      :subtitle="$t('please_select_restaurant_to_view_data')"
    />

    <income-table
      v-else
      :data="data"
      :loading="isLoading"
      :search-query="searchQuery"
      @show-details="showOrderDetails($event.order, $event.item)"
    />

    <!-- Order Details Dialog -->
    <guest-order-details-dialog
      v-model="orderDetailsDialog"
      :order="selectedOrder"
      :guest="selectedGuest"
      :changing-status-order-id="changingStatusOrderId"
      @change-status="changeOrderStatus($event.order, $event.guest, $event.newStatus)"
    />
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import IncomeTable from "@/components/guest/IncomeTable.vue";
import reportService from "@/services/report";
import GuestOrderDetailsDialog from "@/components/dialogs/GuestOrderDetailsDialog.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import { orderTotal } from "@/utils/guestOrderTotal";

export default {
  name: "AdminIncomeTab",
  mixins: [hasPrinting, hasExcel, hasSnackbar],
  components: {
    EmptyState,
    IncomeTable,
    GuestOrderDetailsDialog,
  },
  props: {
    searchQuery: String,
    dateRange: Array,
    isActiveTab: Boolean,
    selectedRestaurants: Array,
    restaurantIds: String,
  },
  data() {
    return {
      data: [],
      companies: [],
      isLoading: false,
      orderDetailsDialog: false,
      selectedOrder: null,
      selectedGuest: null,
      changingStatusOrderId: null,
    };
  },
  computed: {
    restaurantNames() {
      if (this.selectedRestaurants && this.selectedRestaurants.length > 0) {
        return this.selectedRestaurants.map((r) => r.name).join(", ");
      }
      return "Admin";
    },
    restaurantSummary() {
      if (!this.selectedRestaurants || this.selectedRestaurants.length === 0) {
        return "";
      }
      const total = this.selectedRestaurants.length;
      return `${this.$t("restaurants")} (${total})`;
    },
    restaurantNamesForFooter() {
      if (!this.selectedRestaurants || this.selectedRestaurants.length === 0) {
        return "";
      }
      return this.selectedRestaurants.map((r) => r.name).join(", ");
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("date"), value: "date", sortable: false },
        {
          text: this.$t("number_of_orders"),
          value: "orderCount",
          sortable: false,
          align: "center",
        },
        { text: this.$t("amount"), value: "amount", sortable: false, align: "right" },
        { text: "", value: "data-table-expand" },
      ];
    },
    totalIncome() {
      return this.data.reduce((sum, item) => sum + item.amount, 0);
    },
    totalOrderCount() {
      return this.data.reduce((sum, item) => sum + item.orders.length, 0);
    },
    companySummary() {
      if (!this.companies || this.companies.length === 0) {
        return "";
      }
      const total = this.companies.length;
      return `${this.$t("companies")} (${total})`;
    },
    companyNamesForFooter() {
      if (!this.companies || this.companies.length === 0) {
        return "";
      }
      return this.companies.join(", ");
    },
  },
  watch: {
    isPrinting(isPrinting) {
      if (!isPrinting || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.printIncomeTable();
    },
    isExcelDownloading(isDownloading) {
      if (!isDownloading || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.exportIncomeExcel();
    },
    dateRange: {
      handler() {
        if (this.restaurantIds) {
          this.loadData();
        }
      },
    },
    restaurantIds: {
      handler() {
        if (this.restaurantIds) {
          this.loadData();
        }
      },
    },
  },
  methods: {
    async loadData() {
      if (!this.restaurantIds || !this.dateRange || this.dateRange.length !== 2) {
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          date: this.dateRange[0],
          date2: this.dateRange[1],
          restaurants: this.restaurantIds,
        };

        const response = await reportService.getCompanyMultipleNoContract(params);

        this.processData(response.data);
      } catch (error) {
        console.error("Error loading income data:", error);
        this.data = [];
      } finally {
        this.isLoading = false;
      }
    },
    processData(data) {
      const dailyIncome = {};
      const companiesSet = new Set();

      // Check if data.orders exists
      const orders = data.orders || data;

      // Iterate through orders grouped by date
      Object.keys(orders).forEach((date) => {
        const usersOrders = orders[date];
        let dayTotal = 0;
        const dayOrders = [];

        // Iterate through each user for this date
        usersOrders.forEach((user) => {
          // Track company if exists
          if (user.company_name) {
            companiesSet.add(user.company_name);
          }

          // Iterate through orders_guest for each user
          if (user.orders_guest && Array.isArray(user.orders_guest)) {
            user.orders_guest.forEach((order) => {
              const orderSum = orderTotal(order);

              dayTotal += orderSum;
              dayOrders.push({
                id: order.id,
                delivery_time: order.delivery_time,
                state: order.state,
                total: orderSum,
                transactions: order.order_guest_transactions || [],
                ...order,
                name: user.name,
                lastname: user.lastname,
                phone: user.phone,
                company_name: user.company_name,
              });
            });
          }
        });

        dailyIncome[date] = {
          total: dayTotal,
          orders: dayOrders,
        };
      });

      // Convert to array and sort by date
      this.data = Object.keys(dailyIncome)
        .map((date) => ({
          date: date,
          amount: dailyIncome[date].total,
          orders: dailyIncome[date].orders,
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      // Store unique companies
      this.companies = Array.from(companiesSet).sort();
    },
    showOrderDetails(order, item) {
      this.selectedOrder = order;
      this.selectedGuest = order; // We don't have guest info in this context
      this.orderDetailsDialog = true;
    },
    async changeOrderStatus(order, guest, newStatus) {
      if (order.state === newStatus) return;

      this.changingStatusOrderId = order.id;
      this.isLoading = true;

      const payload = {
        ...order,
        order_id: order.id,
        user_id: guest.id,
        status: newStatus,
        // Backend čita `state`; bez ovoga ostaje staro stanje iz kopije narudžbe (...order).
        state: newStatus,
      };

      try {
        await reportService.changeOrderStatus(payload);
        // Update local state
        order.state = newStatus;
        this.selectedOrder = { ...order };
        this.showSuccessSnackbar(this.$t("status_changed_successfully"));
      } catch (error) {
        console.error("Error changing order status:", error);
        // poruka servera (npr. "The selected status is invalid.") da se vidi koji status ne prima
        const reason = error?.response?.data?.message;
        this.showErrorSnackbar(
          reason ? `${this.$t("status_change_error")}: ${reason}` : this.$t("status_change_error")
        );
      } finally {
        this.changingStatusOrderId = null;
        this.isLoading = false;
      }
    },
    printIncomeTable() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        date: item.date,
        orderCount: item.orders.length,
        amount: `${item.amount.toFixed(2)} ${this.restaurantCurrency}`,
      }));

      // Add total row
      tableItems.push({
        num: "",
        date: `<strong>${this.$t("total").toUpperCase()}</strong>`,
        orderCount: `<strong>${this.totalOrderCount}</strong>`,
        amount: `<strong>${this.totalIncome.toFixed(2)} ${
          this.restaurantCurrency
        }</strong>`,
      });

      const companyInfo = this.companySummary
        ? `<div style="text-align: center; margin-top: 8px;">${this.companySummary}</div>`
        : "";

      let footerHtml = "";
      if (this.selectedRestaurants && this.selectedRestaurants.length > 0) {
        footerHtml += `<div style="margin-top: 16px; font-size: 12px;"><strong>${this.$t(
          "restaurants"
        )}:</strong> ${this.restaurantNamesForFooter}</div>`;
      }
      if (this.companyNamesForFooter) {
        footerHtml += `<div style="margin-top: 8px; font-size: 12px;"><strong>${this.$t(
          "companies"
        )}:</strong> ${this.companyNamesForFooter}</div>`;
      }

      this.printDocument({
        pageTitle: this.$t("income"),
        title: `${this.$t("income")}`,
        subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
          this.dateRange[1]
        )}<h4 style="text-align: center">${this.restaurantSummary}</h4>${companyInfo}`,
        tables: [
          {
            headers: this.headers
              .filter((h) => h.value !== "data-table-expand")
              .map((h) => ({ text: h.text, value: h.value, align: h.align })),
            items: tableItems,
          },
        ],
        afterTableHtml: footerHtml,
      });
    },
    exportIncomeExcel() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        date: item.date,
        orderCount: item.orders.length,
        amount: item.amount.toFixed(2),
      }));

      // Calculate total order count
      const totalOrderCount = this.data.reduce(
        (sum, item) => sum + item.orders.length,
        0
      );

      // Add total row
      tableItems.push({
        num: "",
        date: this.$t("total").toUpperCase(),
        orderCount: totalOrderCount,
        amount: this.totalIncome.toFixed(2),
      });

      const companyInfo = this.companySummary ? ` | ${this.companySummary}` : "";

      let footer = "";
      if (this.selectedRestaurants && this.selectedRestaurants.length > 0) {
        footer += `${this.$t("restaurants")}: ${this.restaurantNamesForFooter}`;
      }
      if (this.companyNamesForFooter) {
        if (footer) footer += " | ";
        footer += `${this.$t("companies")}: ${this.companyNamesForFooter}`;
      }

      this.downloadExcel({
        fileName: `${this.restaurantNames.replace(/,/g, "_") || "admin"}_prihod_${
          this.dateRange[0]
        }_${this.dateRange[1]}.xlsx`,
        sheets: [
          {
            name: this.$t("income"),
            title: `${this.$t("income")}`,
            subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
              this.dateRange[1]
            )} - ${this.restaurantSummary}${companyInfo}`,
            headers: this.headers
              .filter((h) => h.value !== "data-table-expand")
              .map((h) => ({ text: h.text, value: h.value, align: h.align })),
            items: tableItems,
            footer: footer,
          },
        ],
      });
    },
  },
};
</script>
